class Home2LabTestFinder {
  constructor(root) {
    this.root = root;
    this.step = 0;
    this.answers = {};
    this.multiSelection = [];
    this.questions = [
      { title: 'What brings you here today?', sub: 'Pick the closest — you can explore others later.', options: ['Tired all the time', 'Gut problems', 'Hormones & menopause', 'Sexual health check', 'Heart risk', 'Just a general MOT'] },
      { title: 'Any of these sound familiar?', sub: 'Pick all that apply.', multi: true },
      { title: 'How long has this been going on?', sub: '', options: ['Less than 2 weeks', '2–8 weeks', 'Months or longer', 'No symptoms — just checking'] },
      { title: 'How do you feel about sample types?', sub: 'We’ll only recommend what you’re comfortable with.', options: ['Happy with anything', 'Finger-prick blood only', 'Prefer saliva or swab', 'Not sure yet'] },
      { title: 'A little about you', sub: 'Some reference ranges depend on this.', options: ['Female, under 45', 'Female, 45+', 'Male, under 45', 'Male, 45+', 'Prefer not to say'] },
      { title: 'What matters most to you?', sub: '', options: ['The most thorough answer', 'Fastest result', 'Best value'] },
    ];
    this.symptoms = {
      'Gut problems': ['Bloating or pain', 'Diarrhoea or urgency', 'Constipation', 'Blood in stool', 'Reflux / stomach burn', 'None of these'],
      'Tired all the time': ['Low energy all day', 'Poor sleep', 'Low mood', 'Hair loss / feeling cold', 'Breathless easily', 'None of these'],
      default: ['Ongoing for weeks', 'Comes and goes', 'Getting worse', 'Affecting work or sleep', 'None of these'],
    };
    try {
      this.config = JSON.parse(root.querySelector('[data-h2l-finder-config]').textContent);
    } catch (_) {
      this.config = { recommendations: [] };
    }
    document.body.classList.add('h2l-finder-active');
    this.bind();
    this.render();
  }

  bind() {
    this.root.querySelector('[data-h2l-finder-back]').addEventListener('click', () => {
      this.step = Math.max(0, this.step - 1);
      this.multiSelection = [];
      this.render();
    });
    this.root.querySelector('[data-h2l-finder-continue]').addEventListener('click', () => {
      this.answers[`q${this.step}`] = [...this.multiSelection];
      this.step += 1;
      this.multiSelection = [];
      this.render();
    });
    this.root.querySelector('[data-h2l-finder-restart]').addEventListener('click', () => {
      this.step = 0;
      this.answers = {};
      this.multiSelection = [];
      this.render();
    });
  }

  getOptions() {
    if (this.step === 1) return this.symptoms[this.answers.q0] || this.symptoms.default;
    return this.questions[this.step]?.options || [];
  }

  select(label) {
    if (this.questions[this.step].multi) {
      this.multiSelection = this.multiSelection.includes(label)
        ? this.multiSelection.filter((value) => value !== label)
        : [...this.multiSelection, label];
    } else {
      this.answers[`q${this.step}`] = label;
      this.step += 1;
    }
    this.render();
  }

  render() {
    if (this.step >= this.questions.length) return this.renderResults();
    const question = this.questions[this.step];
    const isMulti = Boolean(question.multi);
    this.root.querySelector('[data-h2l-finder-quiz]').hidden = false;
    this.root.querySelector('[data-h2l-finder-results]').hidden = true;
    this.root.querySelector('[data-h2l-finder-count]').textContent = `Question ${this.step + 1} of ${this.questions.length}`;
    this.root.querySelector('[data-h2l-finder-progress]').style.width = `${((this.step + 1) / this.questions.length) * 100}%`;
    this.root.querySelector('[data-h2l-finder-title]').textContent = question.title;
    this.root.querySelector('[data-h2l-finder-sub]').textContent = question.sub;
    this.root.querySelector('[data-h2l-finder-back]').hidden = this.step === 0;
    const continueButton = this.root.querySelector('[data-h2l-finder-continue]');
    continueButton.hidden = !isMulti;
    continueButton.disabled = isMulti && this.multiSelection.length === 0;

    const options = this.root.querySelector('[data-h2l-finder-options]');
    options.replaceChildren(...this.getOptions().map((label) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'h2l-finder__option';
      button.textContent = label;
      button.setAttribute('aria-pressed', isMulti ? String(this.multiSelection.includes(label)) : 'false');
      if (this.multiSelection.includes(label)) button.classList.add('is-selected');
      button.addEventListener('click', () => this.select(label));
      return button;
    }));
  }

  renderResults() {
    const recommendations = this.config.recommendations.filter((item) => item.concern);
    const recommendation = recommendations.find((item) => item.concern === this.answers.q0)
      || recommendations.find((item) => item.concern === 'Just a general MOT')
      || recommendations[0];
    if (!recommendation) return;

    let primary = recommendation.primary;
    let alternatives = recommendation.alternatives.filter((item) => item?.name);
    if (recommendation.bloodAlternative && this.answers.q3 === 'Finger-prick blood only') {
      primary = this.config.bloodFallback;
      alternatives = [recommendation.primary, ...alternatives].slice(0, 2);
    }

    this.root.querySelector('[data-h2l-finder-quiz]').hidden = true;
    this.root.querySelector('[data-h2l-finder-results]').hidden = false;
    this.root.querySelector('[data-h2l-result-headline]').textContent = recommendation.headline;
    this.root.querySelector('[data-h2l-result-why]').textContent = recommendation.why;
    this.root.querySelector('[data-h2l-result-primary]').innerHTML = this.primaryCard(primary);
    this.root.querySelector('[data-h2l-result-alternatives]').innerHTML = alternatives.map((item) => this.alternativeCard(item)).join('');
    this.root.querySelector('[data-h2l-finder-results]').focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  escape(value = '') {
    const node = document.createElement('div');
    const decoder = document.createElement('textarea');
    decoder.innerHTML = value;
    node.textContent = decoder.value;
    return node.innerHTML;
  }

  primaryCard(item) {
    return `<article class="h2l-finder__primary"><span class="h2l-finder__best">BEST MATCH</span><div class="h2l-finder__badges"><span>${this.escape(item.sample)} sample</span><span>${this.escape(item.turnaround)}</span></div><h2>${this.escape(item.name)}</h2><p>${this.escape(item.reason)}</p><div class="h2l-finder__product-foot"><strong>${this.escape(item.price)}</strong><a class="h2l-button h2l-button--primary" href="${this.escape(item.url)}">View test &rarr;</a></div></article>`;
  }

  alternativeCard(item) {
    return `<a class="h2l-finder__alternative" href="${this.escape(item.url)}"><span><strong>${this.escape(item.name)}</strong><small>${this.escape(item.reason)}</small></span><b>${this.escape(item.price)}</b></a>`;
  }
}

const bootHome2LabFinder = (scope = document) => {
  scope.querySelectorAll('[data-h2l-test-finder]:not([data-h2l-ready])').forEach((root) => {
    root.dataset.h2lReady = 'true';
    new Home2LabTestFinder(root);
  });
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bootHome2LabFinder(), { once: true });
else bootHome2LabFinder();
document.addEventListener('shopify:section:load', (event) => bootHome2LabFinder(event.target));
